import { useRef, useState } from "react";

/**
 * Generic collage-style page renderer — multiple photo slots support karta hai.
 * `page.collageData` structure:
 * { backLayerUrl, frontLayerUrl }
 * `page.frames` = [{ id, x, y, w, h, rotate, image, imgOffset, imgZoom }, ...]
 * `page.texts` = [{ id, x, y, w, content, font, size, color, align }]
 */
export default function CollageCanvas({
  page, selectedFrame, editingText,
  onPhotoClick, onFrameDrop, onFrameUpdate,
  onTextDblClick, onTextChange, onTextBlur,
  readonly,
}) {
  const { backLayerUrl, frontLayerUrl } = page.collageData || {};

  return (
    <div
      id={`pb-page-${page.id}`}
      className="relative w-full h-full overflow-hidden select-none"
      style={{ background: page.bg }}
    >
      {/* Back layer — decorative background */}
      {backLayerUrl && (
        <img src={backLayerUrl} alt="" draggable={false}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none" />
      )}

      {/* Photo slots — dynamic, jitne bhi admin ne banaye */}
      {page.frames.map((frame) => (
        <PhotoSlot
          key={frame.id}
          frame={frame}
          selected={selectedFrame === frame.id}
          onPhotoClick={onPhotoClick}
          onFrameDrop={onFrameDrop}
          onFrameUpdate={onFrameUpdate}
          readonly={readonly}
        />
      ))}

      {/* Front layer — jo photo ke UPAR overlap karta hai (flower, tape, etc.) */}
      {frontLayerUrl && (
        <img src={frontLayerUrl} alt="" draggable={false}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none" />
      )}

      {/* Text slots — dynamic, jitne bhi admin ne set kiye */}
      {(page.texts || []).map((text, i) => (
        <div key={text.id ?? i} className="absolute" style={{
          left: `${text.x}%`, top: `${text.y}%`, width: `${text.w}%`, textAlign: text.align || "center",
        }}>
          {!readonly && editingText === (text.id ?? i) ? (
            <input autoFocus type="text" value={text.content}
              onChange={(e) => onTextChange && onTextChange(text.id ?? i, e.target.value)}
              onBlur={() => onTextBlur && onTextBlur()}
              className="w-full bg-transparent outline-none"
              style={{ fontFamily: text.font, fontSize: text.size, color: text.color, textAlign: text.align || "center" }} />
          ) : (
            <div onClick={() => !readonly && onTextDblClick && onTextDblClick(text.id ?? i)}
              className="w-full cursor-pointer whitespace-pre-line"
              style={{ fontFamily: text.font, fontSize: text.size, color: text.color }}>
              {text.content}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PhotoSlot({ frame, selected, onPhotoClick, onFrameDrop, onFrameUpdate, readonly }) {
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef(null);
  const imgOffset = frame.imgOffset || { x: 0, y: 0 };
  const imgZoom = frame.imgZoom || 1;

  const handleMouseDown = (e) => {
    if (!frame.image || readonly) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX - imgOffset.x, y: e.clientY - imgOffset.y };
  };
  const handleMouseMove = (e) => {
    if (!dragging || !dragStart.current) return;
    onFrameUpdate && onFrameUpdate(frame.id, { imgOffset: { x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y } });
  };
  const handleMouseUp = () => { setDragging(false); dragStart.current = null; };

  return (
    <div className="absolute overflow-hidden" style={{
      left: `${frame.x}%`, top: `${frame.y}%`, width: `${frame.w}%`, height: `${frame.h}%`,
      transform: `rotate(${frame.rotate || 0}deg)`,
      background: frame.image ? "transparent" : "#000",
      cursor: frame.image ? (dragging ? "grabbing" : "grab") : "pointer",
    }}
      onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
      onClick={() => !readonly && onPhotoClick && onPhotoClick(frame.id)}
      onMouseDown={handleMouseDown}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); if (readonly || !onFrameDrop) return; const url = e.dataTransfer.getData("imageUrl"); onFrameDrop(frame.id, url ? null : e.dataTransfer.files[0], url || null); }}
    >
      {frame.image ? (
        <img src={frame.image} alt="" draggable={false}
          className="absolute w-full h-full object-cover select-none"
          style={{ transform: `translate(${imgOffset.x}px, ${imgOffset.y}px) scale(${imgZoom})`, transformOrigin: "center" }} />
      ) : (!readonly && (
        <div className="w-full h-full flex items-center justify-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.2" className="w-6 h-6 opacity-70">
            <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" />
          </svg>
        </div>
      ))}
      {selected && !readonly && (
        <div className="absolute pointer-events-none" style={{ inset: -2, border: "1.5px solid #888" }} />
      )}
    </div>
  );
}